import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { firestoreService } from "@/services/firestoreService";
import { Timestamp } from "firebase/firestore";
import { Pagination } from "@/components/ui/Pagination";
import { DateRangeFilter } from "@/components/ui/DateRangeFilter";
import { Input } from "@/components/ui/Input";

interface Filing {
  id: string;
  companyId: string;
  companyName?: string;
  ticker?: string;
  filingType: string;
  date: any;
  quarter?: string;
  year?: number;
  sourceUrl?: string;
}

interface Company {
  id: string;
  name: string;
  ticker: string;
}

export const Filings: React.FC = () => {
  const navigate = useNavigate();
  const [filings, setFilings] = useState<Filing[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTicker, setSelectedTicker] = useState("");
  const [filingTypeFilter, setFilingTypeFilter] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadFilings();
  }, [currentPage, itemsPerPage, startDate, endDate, selectedTicker, filingTypeFilter]);

  const loadFilings = async () => {
    try {
      setLoading(true);
      setError(null);
      const constraints: any[] = [];

      if (selectedTicker) {
        constraints.push(
          firestoreService.where("companyId", "==", selectedTicker.toUpperCase())
        );
      }

      if (filingTypeFilter) {
        constraints.push(
          firestoreService.where("filingType", "==", filingTypeFilter)
        );
      }

      if (startDate) {
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        constraints.push(
          firestoreService.where("date", ">=", Timestamp.fromDate(start))
        );
      }

      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        constraints.push(
          firestoreService.where("date", "<=", Timestamp.fromDate(end))
        );
      }

      constraints.push(firestoreService.orderBy("date", "desc"));
      constraints.push(firestoreService.limit(itemsPerPage * currentPage));

      const allFilings = await firestoreService.getDocuments<Filing>(
        "filings",
        constraints
      );

      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedFilings = allFilings.slice(startIndex, endIndex);

      const filingsWithCompany = await Promise.all(
        paginatedFilings.map(async (filing) => {
          const cid = (filing.companyId || "").toString();
          try {
            const company = await firestoreService.getDocument<Company>(
              "companies",
              cid
            );
            return {
              ...filing,
              companyName: company?.name || "Unknown",
              ticker: company?.ticker || cid || "",
            };
          } catch {
            return {
              ...filing,
              companyName: "Unknown",
              ticker: cid || "",
            };
          }
        })
      );

      const filtered = searchQuery
        ? filingsWithCompany.filter(
            (f) =>
              f.companyName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
              f.ticker?.toLowerCase().includes(searchQuery.toLowerCase())
          )
        : filingsWithCompany;

      setFilings(filtered);
      setTotalPages(Math.ceil(allFilings.length / itemsPerPage));
    } catch (err) {
      console.error("Failed to load filings:", err);
      setError(err instanceof Error ? err.message : "Failed to load filings.");
      setFilings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setStartDate("");
    setEndDate("");
    setSelectedTicker("");
    setSearchQuery("");
    setFilingTypeFilter("");
    setCurrentPage(1);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        <div className="mb-8">
          <h1 className="h1 text-4xl mb-2">Financial Reports</h1>
          <p className="body-text text-gray-600">
            Browse all financial filings (10-K, 10-Q, etc.) across all companies
          </p>
        </div>

        <Card className="mb-6 p-6">
          <div className="space-y-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-[200px]">
                <label className="body-text text-sm text-gray-600 mb-1 block">
                  Search Company
                </label>
                <Input
                  type="text"
                  placeholder="Company name or ticker"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>
              <div className="min-w-[200px]">
                <label className="body-text text-sm text-gray-600 mb-1 block">
                  Filter by Ticker
                </label>
                <Input
                  type="text"
                  placeholder="e.g., AAPL"
                  value={selectedTicker}
                  onChange={(e) => {
                    setSelectedTicker(e.target.value.toUpperCase());
                    setCurrentPage(1);
                  }}
                />
              </div>
              <div className="min-w-[150px]">
                <label className="body-text text-sm text-gray-600 mb-1 block">
                  Filing Type
                </label>
                <select
                  value={filingTypeFilter}
                  onChange={(e) => {
                    setFilingTypeFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blazerex-green"
                >
                  <option value="">All Types</option>
                  <option value="10-K">10-K</option>
                  <option value="10-Q">10-Q</option>
                  <option value="8-K">8-K</option>
                  <option value="Annual Report">Annual Report</option>
                </select>
              </div>
              <DateRangeFilter
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={(date) => {
                  setStartDate(date);
                  setCurrentPage(1);
                }}
                onEndDateChange={(date) => {
                  setEndDate(date);
                  setCurrentPage(1);
                }}
                onClear={() => {
                  setStartDate("");
                  setEndDate("");
                  setCurrentPage(1);
                }}
              />
              <Button variant="outline" onClick={handleClearFilters}>
                Clear All
              </Button>
            </div>
          </div>
        </Card>

        {error ? (
          <Card className="p-6">
            <p className="body-text text-red-600 mb-2">{error}</p>
            <Button variant="outline" onClick={() => { setError(null); loadFilings(); }}>Try again</Button>
          </Card>
        ) : loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blazerex-green"></div>
          </div>
        ) : filings.length === 0 ? (
          <Card>
            <div className="text-center py-12">
              <p className="body-text text-gray-600">
                No filings found. Try adjusting your filters.
              </p>
            </div>
          </Card>
        ) : (
          <>
            <div className="space-y-4 mb-6">
              {filings.map((filing) => (
                <Card
                  key={filing.id}
                  hover
                  className="cursor-pointer"
                  onClick={() =>
                    navigate(`/companies/${(filing.ticker || filing.companyId || "").toUpperCase()}?filing=${filing.id}`)
                  }
                >
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="body-text font-mono text-blazerex-green font-medium">
                          {filing.ticker}
                        </span>
                        <span className="sub-header text-xl">
                          {filing.companyName}
                        </span>
                        <span className="px-2 py-1 bg-gray-100 rounded text-sm">
                          {filing.filingType}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600 flex-wrap">
                        {filing.quarter && filing.year && (
                          <span>
                            {filing.quarter} {filing.year}
                          </span>
                        )}
                        {filing.date && (
                          <span>
                            {new Date(
                              filing.date.toDate?.() || filing.date
                            ).toLocaleDateString()}
                          </span>
                        )}
                        {filing.sourceUrl && (
                          <a
                            href={filing.sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blazerex-green hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            View on SEC.gov
                          </a>
                        )}
                      </div>
                    </div>
                    <Button variant="outline" size="sm">
                      View & Summarise
                    </Button>
                  </div>
                </Card>
              ))}
            </div>

            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                itemsPerPage={itemsPerPage}
                onItemsPerPageChange={(items) => {
                  setItemsPerPage(items);
                  setCurrentPage(1);
                }}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

